import type { ScheduledRecording } from '@mimir/common/dto/scheduledRecording';
import type { FuzzyValue, FuzzyBounds } from '@mimir/common/util/fuzzyNumbers';
import type { ItemStateType } from '@mimir/common/dto/itemState';
import type { LanguageCode } from '@mimir/common/dto/asyncOperationItemCreationTypes';
import type { AsyncOperationState } from '@mimir/common/dto/asyncOperationPrimitives';
import type { ProgressInfo } from '@mimir/common/dto/progress';
import type { SearchItemType } from '@mimir/common/dto/itemTypes';
import type { MediaAvailability } from '@mimir/common/dto/mediaAvailability';
import type { ExternalProxyDto } from '@mimir/common/dto/externalProxyDto';
import type { AafLocationUpdatePayload } from '@mimir/common/dto/aafDto';
import type { EditProxyFormat, Ingest, GeneratedContentLocation } from '@mimir/common/dto/itemPropTypes';
import type { ItemAccessControl } from '@mimir/common/dto/itemAccessControlDto';
import type { ItemMetaData, ItemMetaDataDto } from '@mimir/common/dto/fieldValue';
import type { VirtualClipModel } from '@mimir/common/dto/virtualClipModel';
import type { TimedMetadataCollection, UsageRuleSearchDocument } from '@mimir/common/dto/timedMetadataModels';
import type { GeoShape } from '@mimir/common/dto/GeoLocation';
import type { LivePreviewLocation } from '@mimir/common/dto/livePreviewLocation';
import type { Celebrity, Label, Person } from '@mimir/common/dto/searchModels';

type SupportedOutgoingAction = 'mimir_loaded' | 'selected' | 'deselected' | 'updated_subtitles' | 'import';
export const postIncomingMessageActions = <const>['enable_rich_payloads', 'enable_native_drag_data', 'open_folder', 'open_item', 'request_item', 'search', 'set_work_folder', 'sticky_search'];
export type SupportedIncomingAction = typeof postIncomingMessageActions[number];

/**
 * We must keep these properties for backwards compatibility with DiNA.
 */
interface PostMessageLegacyProps {
  self: string;
  title: string;
  description: string;
  itemCreatedOn: string;
  mediaRecordedOn: string;
  provider: 'Mimir';
}

/**
 * A search hit or single Mimir item suitable for sending as a post-message
 */
export type PostMessageItem = PostMessageLegacyProps & {
  id: string;
  createdOn: string | undefined;
  itemState: ItemStateType;
  isDeleting: boolean;
  transcodingState: AsyncOperationState | undefined;
  progressInfo: ProgressInfo | undefined;
  originalFileName: string | undefined;
  itemType: SearchItemType;
  folderParents: string[] | undefined;
  archiveFileTransferState: AsyncOperationState | undefined;
  media: MediaAvailability | undefined;
  externalProxyInfo: ExternalProxyDto | undefined;
  aafLocation: AafLocationUpdatePayload | undefined;
  ingest: Ingest | undefined;
  mediaType: string | undefined;
  mediaFrameRate: number | undefined;
  editProxyFormat: EditProxyFormat | undefined;
  modifiedOn: string | undefined;
  isLive: boolean;
  generatedContentLocation: GeneratedContentLocation | undefined;
  visibleTo: ItemAccessControl | undefined;
  updatableBy: ItemAccessControl | undefined;
  scheduledRecording: ScheduledRecording | undefined;
  name: string | undefined;
  metadata: ItemMetaDataDto;
  mediaDuration: number | undefined;
  thumbnail: string | undefined;
  thumbnailStrip: string | undefined;
  ingestSourceFullPath: string | undefined;
  ingestSourceS3Bucket: string | undefined;
  ingestSourceStorageId: string | undefined;
  createdBy: string | undefined;
  owner: string | undefined;
  languageCode: LanguageCode | undefined;
  isArchived: boolean;
  geoLocation: GeoShape | undefined;
  virtualClipInfo: VirtualClipModel | undefined;
  warnings: string[] | undefined;
  hasProxy: boolean;
  hasEditProxy: boolean;
  mediaSize: number | undefined;
  proxySize: number | undefined;
  parent: string | undefined;
  mediaWidth: number | undefined;
  mediaHeight: number | undefined;
  hasRestrictedMaterial: boolean | undefined;
  usageRules: UsageRuleSearchDocument[] | undefined;
  exiftags: string | undefined;
  technicalMetadata: ItemMetaData | undefined;
  highRes: string | undefined;
  editProxyUrl: string | undefined;
  proxy: string | undefined;
  aaf: string | undefined;
  srtUrl: string | undefined;
  translationSrtUrl: string | undefined;
  vttUrl: string | undefined;
  translationVttUrl: string | undefined;
  livePreviewLocation: LivePreviewLocation | undefined;
  detectedLabels: Label[] | undefined;
  detectedPersons: Person[] | undefined;
  detectedCelebrities: Celebrity[] | undefined;
};

export interface ReceivedMessage {
  destination: string;
  action: SupportedIncomingAction;
  payload?: string;
}

export interface PostMessage {
  source: 'MIMIR',
  action: SupportedOutgoingAction;
  targetOrigin: string;
  user: string;
  payload?: any;
}

type PostMessageItemTypes =
  | 'search-hit' // A single search result. Will have a limited number of properties instead of the full item object
  | 'full-item'; // Fully loaded item

/**
 * Defines shape of payload sent when import button in the item-details page is clicked
 */
export interface ImportItemEvent {
  action: 'import';
  payload: {
    /**
     * The full JSON for the item this message is for
     */
    item: PostMessageItem;
    /**
     * Media selection range if mark-in and out point is selected in the timeline
     */
    range: FuzzyBounds | undefined;
    /**
     * Duration of a single frame
     */
    frameDuration: FuzzyValue;

    /**
     * Items sent via post message can be coming from different contexts like the search page or
     * the item details page. This property specifies which type of item it is.
     */
    type: PostMessageItemTypes;
  }
}

export interface GenericEvent {
  action: 'mimir_loaded' | 'selected' | 'deselected' | 'updated_subtitles';
  payload: PostMessageItem;
}

export type RichPostMessageItem = PostMessageItem & {
  timedMetadata: TimedMetadataCollection | null;
};
